/**
 * `/agents/sessions` — the read-only session surface (SI-G2, release plan
 * XS-G10). Closes the gap named by SI-G2: zero UI files touched the session
 * tables and there was no list / trajectory / replay surface or nav entry.
 *
 *  · G2a — a read-only DataTable of `excellent_live_sessions` (list contract +
 *    READ mount), with a nav entry under the Agents rail.
 *  · G2b — a trajectory detail view (`/agents/sessions/$id`) with a TOMBSTONE
 *    MARKER: a compacted session (`snapshot_floor > 0` / `payload_tombstoned_at`
 *    rows) must NEVER render as "the agent did nothing". Compaction is the
 *    storage substrate doing its job, not evidence of inactivity.
 *
 * Both legs read only (READ authz) — no writes on this surface.
 */

import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  formatDateTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  sessions,
  type SessionListRow,
  type SessionTrajectoryActionRow,
  type SessionTrajectoryResponse,
} from "@excellent/ui/api/contracts/sessions";
import { rootRoute } from "./__root";

const NUM = "tabular-nums";
const MUTED = "type-subtext text-muted-foreground";

function SessionsListPage() {
  const { data, error, isLoading } = useApi(sessions.list, { query: {} });

  const cols: DataTableColumn<SessionListRow>[] = [
    {
      key: "title",
      header: "Session",
      /**
       * R3 (#45973): TRUNCATE. A session title is an agent-run label and can be
       * a whole task title — one `RO-SCOPE:out …` row was wide enough to push
       * Kind/Actions/State/Participants/Created entirely off a 1440px viewport,
       * leaving this the only visible column. Full text stays in `title`.
       */
      render: (r) => (
        <Link
          href={`/agents/sessions/${r.id}`}
          className="block max-w-[42ch] truncate font-medium text-foreground hover:underline"
          title={r.title ?? `#${r.id}`}
        >
          {r.title ? r.title : `#${r.id}`}
        </Link>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      render: (r) => <Badge variant="outline">{r.kind}</Badge>,
    },
    {
      key: "originTaskId",
      header: "Origin",
      render: (r) =>
        r.hasNullOrigin ? (
          <IntentBadge intent="info" withDot>
            stranger
          </IntentBadge>
        ) : (
          <span className={NUM}>#{r.originTaskId}</span>
        ),
    },
    {
      key: "headSeq",
      header: "Actions",
      render: (r) => (
        <span className={`${NUM} text-foreground`}>
          {r.actionCount}
          <span className={MUTED}> / head {r.headSeq}</span>
        </span>
      ),
    },
    {
      key: "toolCallCount",
      header: "Trajectory",
      render: (r) => (
        <span className={`${NUM} text-foreground`}>
          {r.toolCallCount}
          <span className={MUTED}> tool</span>
          {r.runUsageCount > 0 ? (
            <span className={MUTED}> · usage {r.runUsageCount}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "snapshotFloor",
      header: "State",
      render: (r) =>
        r.snapshotFloor > 0 ? (
          <IntentBadge intent="warning" withDot>
            compacted (floor {r.snapshotFloor})
          </IntentBadge>
        ) : (
          <span className={MUTED}>live</span>
        ),
    },
    {
      key: "participantCount",
      header: "Participants",
      render: (r) => <span className={`${NUM} text-foreground`}>{r.participantCount}</span>,
    },
    {
      key: "createdAt",
      header: "Created",
      render: (r) => <span className={MUTED}>{formatDateTime(r.createdAt)}</span>,
    },
  ];

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Sessions"
        description="A read-only view of the live-session substrate — every recorded session and its trajectory. Nothing here can be mutated from this surface."
      />
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading sessions…</p>
      ) : error && !data ? (
        <p className="type-body text-destructive">{String(error.message ?? error)}</p>
      ) : (
        <DataTable
          title={`${data?.sessions.length ?? 0} session(s)`}
          rows={data?.sessions ?? []}
          columns={cols}
          rowKey={(r) => String(r.id)}
          empty="No sessions recorded yet."
        />
      )}
    </div>
  );
}

/** Render ONE trajectory action, honouring the G2b tombstone marker. */
function TrajectoryActionRow({ action }: { action: SessionTrajectoryActionRow }) {
  if (action.tombstoned) {
    // Compacted payload — it existed, but the body was rolled into a snapshot
    // to bound storage. MUST NOT read as "the agent did nothing".
    return (
      <li className="flex flex-col gap-1 py-2">
        <div className="flex flex-wrap items-center gap-2 type-body">
          <span className={`${NUM} text-muted-foreground`}>#{action.seq}</span>
          <span className="font-medium text-foreground">{action.kind}</span>
          <IntentBadge intent="warning" withDot>
            compacted — payload unavailable
          </IntentBadge>
          <span className={MUTED}>{action.actorId}</span>
        </div>
        {action.payloadHash ? (
          <span className={`${MUTED} font-mono`}>hash {action.payloadHash.slice(0, 16)}…</span>
        ) : null}
      </li>
    );
  }
  const payload = action.payload;
  const summary =
    payload && typeof payload === "object"
      ? JSON.stringify(payload).slice(0, 120)
      : payload !== undefined && payload !== null
        ? String(payload).slice(0, 120)
        : "";
  return (
    <li className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center gap-2 type-body">
        <span className={`${NUM} text-muted-foreground`}>#{action.seq}</span>
        <span className="font-medium text-foreground">{action.kind}</span>
        <span className={MUTED}>{action.actorId}</span>
      </div>
      {summary ? <span className={`${MUTED} font-mono`}>{summary}</span> : null}
    </li>
  );
}

const trajectorySearch = z.object({
  urn: z.string().optional(),
});

function SessionTrajectoryPage() {
  const { id } = SessionTrajectoryRoute.useParams();
  const search = SessionTrajectoryRoute.useSearch();
  // Prefer an explicit ?urn= (read-only authoring), else derive from the id.
  const urn = search.urn ?? `live_session:${id}`;
  const { data, error, isLoading } = useApi(sessions.trajectory, {
    query: { urn },
  });

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title={data ? data.title || `Session #${id}` : `Session #${id}`}
        description={
          data
            ? `${data.kind} · ${data.summary}${data.citation ? ` · ${data.citation}` : ""}`
            : "Loading trajectory…"
        }
      />
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading trajectory…</p>
      ) : error && !data ? (
        <p className="type-body text-destructive">{String(error.message ?? error)}</p>
      ) : (
        <>
          {(data as SessionTrajectoryResponse).compacted ? (
            <Card>
              <CardHeader>
                <CardTitle>Compacted session</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="type-body text-muted-foreground">
                  This session was compacted (snapshot floor{" "}
                  {(data as SessionTrajectoryResponse).snapshotFloor}). Older
                  action payloads were rolled into a snapshot; they are shown as{" "}
                  <span className="text-foreground">compacted</span> rather than
                  absent — compaction is storage doing its job, not evidence the
                  agent did nothing.
                </p>
              </CardContent>
            </Card>
          ) : null}

          {(data as SessionTrajectoryResponse).actions.length === 0 ? (
            <p className="type-body text-muted-foreground">
              Empty trajectory — no actions recorded for this session.
            </p>
          ) : (
            <Card className="gap-0 overflow-hidden py-0">
              <CardContent className="py-4">
                <ul className="divide-y divide-border">
                  {(data as SessionTrajectoryResponse).actions.map((a) => (
                    <TrajectoryActionRow key={a.seq} action={a} />
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export const agentsSessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/sessions",
  validateSearch: trajectorySearch.optional(),
  component: SessionsListPage,
});

export const SessionTrajectoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/sessions/$id",
  validateSearch: trajectorySearch,
  component: SessionTrajectoryPage,
});
